import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import ProcessSection from '../components/sections/ProcessSection';
import ReferralTierComparison from '../components/sections/ReferralTierComparison';
import { ReferralCtaBanner } from '../components/ui/ReferralPromoAd';
import {
  CASE_STUDIES,
  CERTIFICATIONS,
  ENGAGEMENT_MODELS,
  ENGINEERING_SERVICES,
  HERO_WORDS,
  PARTNERS,
  STATS,
} from '../config/homePageData';

function useCounter(target, duration = 1800) {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const steps = 60;
        let i = 0;
        const tick = setInterval(() => {
          i += 1;
          const ease = 1 - Math.pow(1 - i / steps, 3);
          setVal(Math.floor(target * ease));
          if (i >= steps) {
            setVal(target);
            clearInterval(tick);
          }
        }, duration / steps);
      }
    }, { threshold: 0.3 });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return [val, ref];
}

function useTypewriter(words, speed = 90, pause = 1800) {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[wordIdx];
    const delay = deleting ? speed / 2 : charIdx === word.length ? pause : speed;

    const timer = setTimeout(() => {
      if (!deleting && charIdx < word.length) {
        setDisplay(word.slice(0, charIdx + 1));
        setCharIdx((current) => current + 1);
      } else if (!deleting && charIdx === word.length) {
        setDeleting(true);
      } else if (deleting && charIdx > 0) {
        setDisplay(word.slice(0, charIdx - 1));
        setCharIdx((current) => current - 1);
      } else {
        setDeleting(false);
        setWordIdx((current) => (current + 1) % words.length);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [charIdx, deleting, pause, speed, wordIdx, words]);

  return display;
}

function StatCard({ stat }) {
  const [val, ref] = useCounter(stat.value);

  return (
    <div ref={ref} style={{ textAlign: 'center', padding: '30px 22px' }}>
      <div style={{ fontFamily: 'var(--font-h)', fontSize: 40, fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>
        {val}{stat.suffix}
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, fontFamily: 'var(--font-m)' }}>
        {stat.label}
      </div>
    </div>
  );
}

function ServiceCard({ svc }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={svc.link}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="service-card"
      style={{
        display: 'block',
        minHeight: 266,
        padding: '30px 28px',
        background: hovered ? '#0f172a' : 'var(--bg2)',
        border: '1px solid',
        borderColor: hovered ? '#0f172a' : 'var(--border)',
        borderRadius: 8,
        textDecoration: 'none',
        transition: 'all 0.25s ease',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered ? '0 20px 40px -12px rgba(15,23,42,0.28)' : 'none',
      }}
    >
      <div style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        display: 'grid',
        placeItems: 'center',
        background: hovered ? 'rgba(255,255,255,0.14)' : 'rgba(37,99,235,0.09)',
        color: hovered ? '#fff' : 'var(--teal)',
        fontFamily: 'var(--font-m)',
        fontSize: 13,
        fontWeight: 800,
        marginBottom: 22,
      }}>
        {svc.icon}
      </div>
      <div style={{
        fontFamily: 'var(--font-h)',
        fontWeight: 800,
        fontSize: 18,
        color: hovered ? '#fff' : 'var(--text)',
        marginBottom: 12,
      }}>
        {svc.title}
      </div>
      <p style={{
        fontSize: 14,
        color: hovered ? 'rgba(255,255,255,0.82)' : 'var(--muted)',
        lineHeight: 1.7,
        marginBottom: 24,
      }}>
        {svc.desc}
      </p>
      <div style={{ fontSize: 13, fontWeight: 700, color: hovered ? '#fff' : 'var(--teal)' }}>
        Learn more -&gt;
      </div>
    </Link>
  );
}

const proofPoints = ['Discovery in 48 hours', 'Security-first delivery', 'Bangladesh & global clients'];

export default function HomePage() {
  const typedWord = useTypewriter(HERO_WORDS);

  return (
    <div style={{ overflowX: 'hidden' }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.85)} }

        .fade-up-1 { animation: fadeUp 0.7s 0.1s ease both; }
        .fade-up-2 { animation: fadeUp 0.7s 0.25s ease both; }
        .fade-up-3 { animation: fadeUp 0.7s 0.4s ease both; }
        .fade-up-4 { animation: fadeUp 0.7s 0.55s ease both; }
        .fade-up-5 { animation: fadeUp 0.7s 0.7s ease both; }

        .cursor {
          display: inline-block;
          width: 3px;
          height: 1em;
          background: var(--teal);
          margin-left: 4px;
          vertical-align: middle;
          animation: blink 0.8s infinite;
        }

        .marquee-track { display:flex; gap:60px; animation:marquee 22s linear infinite; white-space:nowrap; }
        .marquee-wrap { overflow:hidden; }

        .section-label {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          font-family: var(--font-m);
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--teal);
          margin-bottom: 20px;
        }
        .section-label::before {
          content: '';
          display: block;
          width: 28px;
          height: 2px;
          background: var(--teal);
          border-radius: 2px;
        }

        .stat-divider { border-right: 1px solid var(--border); }
        .stat-divider:last-child { border-right: none; }

        .engagement-card,
        .case-card {
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--bg2);
          transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
        }
        .engagement-card { padding: 32px 28px; }
        .case-card { padding: 34px 30px; display:flex; flex-direction:column; gap:16px; }
        .engagement-card:hover,
        .case-card:hover {
          transform: translateY(-4px);
          border-color: rgba(37,99,235,0.35);
          box-shadow: 0 20px 40px -12px rgba(15,23,42,0.14);
        }

        .cert-pill {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 124px;
          min-height: 104px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--bg2);
          text-align: center;
          padding: 16px;
        }

        @media (max-width: 900px) {
          .hero-shell { grid-template-columns: 1fr !important; text-align: center; }
          .hero-proof { justify-content: center; }
          .stats-row { grid-template-columns: repeat(2, 1fr) !important; }
          .stat-divider:nth-child(2) { border-right: none; }
          .stat-divider:nth-child(1), .stat-divider:nth-child(2) { border-bottom: 1px solid var(--border); }
          .services-grid, .cases-grid { grid-template-columns: 1fr !important; }
          .engage-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 640px) {
          .hero-h1 { font-size: 40px !important; }
          .hero-section { padding: 128px 5% 76px !important; }
          .hero-proof { align-items: stretch; flex-direction: column; }
          .engage-grid { grid-template-columns: 1fr !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation: none !important; transition: none !important; }
        }
      `}</style>

      <section className="hero-section" style={{
        padding: '150px 5% 100px',
        minHeight: '88vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)',
          backgroundSize: '64px 64px',
          opacity: 0.45,
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          right: '-18%',
          top: '14%',
          width: 720,
          height: 720,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div className="hero-shell" style={{
          width: '100%',
          maxWidth: 1180,
          margin: '0 auto',
          position: 'relative',
          zIndex: 2,
          display: 'grid',
          gridTemplateColumns: '1.1fr 0.9fr',
          gap: 42,
          alignItems: 'center',
        }}>
          <div>
            <div className="fade-up-1" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(37,99,235,0.22)', background: 'rgba(37,99,235,0.06)', marginBottom: 28 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--teal)', animation: 'pulse 2s infinite', display: 'inline-block' }} />
              <span style={{ fontSize: 12, fontFamily: 'var(--font-m)', color: 'var(--teal)', fontWeight: 700 }}>AI-ready software engineering partner</span>
            </div>

            <h1 className="fade-up-2 hero-h1" style={{
              fontFamily: 'var(--font-h)',
              fontSize: 'clamp(44px,6.2vw,76px)',
              fontWeight: 900,
              color: 'var(--text)',
              lineHeight: 1.03,
              margin: '0 0 28px',
              maxWidth: 720,
            }}>
              Build digital products for <span style={{ color: 'var(--teal)' }}>{typedWord}<span className="cursor" /></span>
            </h1>

            <p className="fade-up-3" style={{
              fontSize: 19,
              color: 'var(--muted)',
              lineHeight: 1.75,
              margin: '0 0 34px',
              maxWidth: 610,
            }}>
              AxentraLab designs, ships, and supports web platforms, automation systems, cloud infrastructure, and AI-enabled applications for teams that need reliable delivery.
            </p>

            <div className="fade-up-4" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 30 }}>
              <Link to="/contact" className="btn-primary" style={{ padding: '16px 34px', fontSize: 15, fontWeight: 700 }}>
                Get Free Estimation -&gt;
              </Link>
              <Link to="/portfolio" className="btn-outline" style={{ padding: '16px 30px', fontSize: 15 }}>
                View Recent Projects
              </Link>
            </div>

            <div className="fade-up-5 hero-proof" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {proofPoints.map((point) => (
                <span key={point} style={{
                  padding: '10px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.72)',
                  color: 'var(--muted)',
                  fontSize: 13,
                  fontWeight: 600,
                }}>
                  {point}
                </span>
              ))}
            </div>
          </div>

          <div className="fade-up-5" style={{
            background: 'rgba(255,255,255,0.86)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 24px 70px -30px rgba(15,23,42,0.35)',
            overflow: 'hidden',
          }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--font-m)', color: 'var(--muted)', fontSize: 12, fontWeight: 700 }}>DELIVERY SNAPSHOT</span>
              <span style={{ color: '#059669', fontSize: 12, fontWeight: 800 }}>ON TRACK</span>
            </div>
            <div className="stats-row" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
            }}>
              {STATS.map((stat) => (
                <div key={stat.label} style={{ borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                  <StatCard stat={stat} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div style={{ padding: '26px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--bg2)', overflow: 'hidden' }}>
        <div className="marquee-wrap">
          <div className="marquee-track">
            {[...PARTNERS, ...PARTNERS].map((partner, index) => (
              <span key={`${partner}-${index}`} style={{ fontSize: 14, fontFamily: 'var(--font-m)', fontWeight: 600, color: 'var(--muted)', opacity: 0.68 }}>
                {partner}
              </span>
            ))}
          </div>
        </div>
      </div>

      <section style={{ padding: '96px 5%', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48, flexWrap: 'wrap', gap: 24 }}>
            <div>
              <div className="section-label">Engineering Services</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(28px,4vw,48px)', fontWeight: 900, color: 'var(--text)', lineHeight: 1.1, maxWidth: 580, margin: 0 }}>
                Practical engineering for product, cloud, AI, and automation work
              </h2>
            </div>
            <Link to="/services" className="btn-outline" style={{ whiteSpace: 'nowrap' }}>
              View all services -&gt;
            </Link>
          </div>

          <div className="services-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {ENGINEERING_SERVICES.map((svc) => (
              <ServiceCard key={svc.title} svc={svc} />
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '76px 5%', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', textAlign: 'center' }}>
          <div className="section-label" style={{ justifyContent: 'center' }}>Standards & Trust</div>
          <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(24px,3.5vw,40px)', fontWeight: 900, color: 'var(--text)', marginBottom: 16 }}>
            Delivery standards your business can rely on
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, maxWidth: 600, margin: '0 auto 42px', lineHeight: 1.7 }}>
            We keep security, documentation, quality checks, and operational readiness in the workflow from the first sprint.
          </p>
          <div style={{ display: 'flex', gap: 18, justifyContent: 'center', flexWrap: 'wrap' }}>
            {CERTIFICATIONS.map((cert) => (
              <div key={cert.label} className="cert-pill">
                <div style={{ fontFamily: 'var(--font-h)', fontWeight: 900, fontSize: 13, color: 'var(--text)' }}>{cert.label}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.4 }}>{cert.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '96px 5%', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ marginBottom: 48 }}>
            <div className="section-label">Client Outcomes</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(28px,4vw,48px)', fontWeight: 900, color: 'var(--text)', lineHeight: 1.1, maxWidth: 560, margin: 0 }}>
              Recent work focused on measurable business value
            </h2>
          </div>
          <div className="cases-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {CASE_STUDIES.map((item) => (
              <div key={item.title} className="case-card">
                <span style={{ fontSize: 12, fontFamily: 'var(--font-m)', fontWeight: 700, color: item.color, textTransform: 'uppercase' }}>{item.tag}</span>
                <div style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 20, color: 'var(--text)', lineHeight: 1.25 }}>{item.title}</div>
                <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, flex: 1 }}>{item.desc}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontFamily: 'var(--font-h)', fontSize: 34, fontWeight: 900, color: item.color }}>{item.metric}</span>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>{item.metricLabel}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 38 }}>
            <Link to="/portfolio" className="btn-outline">View all projects -&gt;</Link>
          </div>
        </div>
      </section>

      <section style={{ padding: '96px 5%', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div className="section-label" style={{ justifyContent: 'center', display: 'flex' }}>Client Voices</div>
          <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(28px,4vw,46px)', fontWeight: 900, color: 'var(--text)', textAlign: 'center', marginBottom: 48 }}>
            Partnerships built on trust
          </h2>
          <TestimonialsSection />
        </div>
      </section>

      <section style={{ padding: '96px 5%', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ marginBottom: 48, textAlign: 'center' }}>
            <div className="section-label" style={{ justifyContent: 'center', display: 'flex' }}>How We Work</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(28px,4vw,48px)', fontWeight: 900, color: 'var(--text)', lineHeight: 1.1, maxWidth: 570, margin: '0 auto 16px' }}>
              Flexible engagement models for every stage
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 16, maxWidth: 500, margin: '0 auto', lineHeight: 1.7 }}>
              Start with one focused specialist or bring in a full product squad. We shape the model around your timeline, risk, and budget.
            </p>
          </div>
          <div className="engage-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
            {ENGAGEMENT_MODELS.map((model) => (
              <div key={model.title} className="engagement-card">
                <div style={{ fontSize: 13, color: model.color, fontFamily: 'var(--font-m)', fontWeight: 700, marginBottom: 12 }}>{model.tag}</div>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 24, fontWeight: 900, color: model.color, marginBottom: 14 }}>{model.icon}</div>
                <div style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 18, color: 'var(--text)', marginBottom: 14 }}>{model.title}</div>
                <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 24 }}>{model.desc}</p>
                <Link to="/contact" style={{ fontSize: 13, fontWeight: 700, color: model.color, textDecoration: 'none' }}>Learn more -&gt;</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '96px 5%', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div className="section-label" style={{ justifyContent: 'center', display: 'flex' }}>How We Deliver</div>
          <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(28px,4vw,46px)', fontWeight: 900, color: 'var(--text)', textAlign: 'center', marginBottom: 48 }}>
            Our delivery framework
          </h2>
          <ProcessSection />
        </div>
      </section>

      <section style={{ padding: '60px 5%', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <ReferralCtaBanner />
          <div style={{ marginTop: 58 }}>
            <ReferralTierComparison />
          </div>
        </div>
      </section>

      <section style={{ padding: '112px 5%', textAlign: 'center', background: 'var(--bg2)', borderTop: '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(37,99,235,0.07) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 650, margin: '0 auto', position: 'relative' }}>
          <div className="section-label" style={{ justifyContent: 'center', display: 'flex' }}>Get Started Today</div>
          <h2 style={{
            fontFamily: 'var(--font-h)',
            fontSize: 'clamp(32px,5vw,58px)',
            fontWeight: 900,
            color: 'var(--text)',
            margin: '0 auto 20px',
            lineHeight: 1.05,
          }}>
            Ready to build, improve, and scale your software?
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: 16, marginBottom: 38, lineHeight: 1.75, maxWidth: 500, margin: '0 auto 38px' }}>
            Share your idea or current technical problem. We will respond with a practical next step, a realistic timeline, and clear pricing.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ padding: '17px 38px', fontSize: 15, fontWeight: 700 }}>
              Get Free Audit -&gt;
            </Link>
            <Link to="/contact" className="btn-outline" style={{ padding: '17px 34px', fontSize: 15 }}>
              Book Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

